import React, { useMemo, useState } from "react";
import { EmptyState } from "../../../../components/ui/PageBits";
import { fmtTime } from "../../bookingService";
import { groupCustomers } from "./ShopBits";
import BookingEditModal from "./BookingEditModal";

/**
 * Customer directory: every customer aggregated from the shop's bookings,
 * with history and quick booking actions per customer.
 */
export default function CustomersTab({ shop, resources, bookings, onStatus, onEdit }) {
    const [q, setQ] = useState("");
    const [selKey, setSelKey] = useState("");
    const [editing, setEditing] = useState(null);

    const mine = useMemo(() => bookings.filter((b) => b.ownerId === shop.id), [bookings, shop.id]);
    const customers = useMemo(() => groupCustomers(mine), [mine]);
    const resName = (id) => resources.find((r) => r.id === id)?.name || "(deleted)";

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return customers;
        return customers.filter((c) =>
            [c.name, c.email, c.phone].join(" ").toLowerCase().includes(needle)
        );
    }, [customers, q]);

    const selected = filtered.find((c) => c.key === selKey) || null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
                <div className="hk-card">
                    <h5 className="hk-card-title">Customers · {customers.length}</h5>
                    <input
                        className="hk-input mb-3"
                        placeholder="Search name, email, phone…"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setSelKey(""); }}
                    />
                    {filtered.length === 0 ? (
                        <EmptyState icon="◎" title="No customers found" hint="Customers appear here after their first booking." />
                    ) : (
                        <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                            {filtered.map((c) => {
                                const active = selKey === c.key;
                                return (
                                    <button
                                        key={c.key}
                                        onClick={() => setSelKey(c.key)}
                                        className={`w-full text-left px-4 py-3 rounded-xl border transition ${active ? "border-[#0f172a] bg-[#f8fafc]" : "border-[#e2e8f0] bg-white hover:border-[#94a3b8]"}`}
                                    >
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="font-bold text-sm truncate">{c.name}</span>
                                            <span className="hk-mono text-xs text-[#64748b] shrink-0">{c.total} ×</span>
                                        </div>
                                        <div className="text-xs text-[#64748b] truncate">
                                            {c.email || "no email"}{c.phone ? ` · ${c.phone}` : ""}
                                        </div>
                                        <div className="flex gap-2 mt-1.5">
                                            {c.upcoming > 0 && <span className="hk-pill hk-pill-green">{c.upcoming} upcoming</span>}
                                            {c.pending > 0 && <span className="hk-pill hk-pill-amber">{c.pending} pending</span>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="lg:col-span-7">
                {!selected ? (
                    <div className="hk-card">
                        <EmptyState icon="👈" title="Pick a customer" hint="Select a customer to see their booking history and manage it." />
                    </div>
                ) : (
                    <div className="hk-card">
                        <div className="flex flex-wrap justify-between items-start gap-2 mb-1">
                            <div>
                                <h5 className="font-bold text-lg leading-tight">{selected.name}</h5>
                                <div className="text-xs text-[#64748b]">
                                    {selected.email || "no email"}{selected.phone ? ` · ${selected.phone}` : ""}
                                </div>
                            </div>
                            <div className="hk-mono text-xs text-[#64748b]">
                                {selected.total} total · {selected.completed} done
                            </div>
                        </div>
                        <div className="space-y-2 mt-3">
                            {selected.bookings.map((b) => (
                                <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 rounded-xl border border-[#e2e8f0]">
                                    <div className="min-w-0">
                                        <div className="text-sm">
                                            <span className="hk-mono text-xs text-[#64748b]">{b.id}</span>{" "}
                                            <strong>{b.date} · {fmtTime(b.start)} – {fmtTime(b.end)}</strong>
                                        </div>
                                        <div className="text-xs text-[#64748b]">{resName(b.resourceId)} · {b.type}</div>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <select
                                            className="hk-select hk-mono"
                                            style={{ width: "auto", textTransform: "capitalize" }}
                                            value={b.status}
                                            title="Change booking status"
                                            onChange={(e) => onStatus(b.id, e.target.value)}
                                        >
                                            {["pending", "confirmed", "completed", "cancelled", "noshow"].map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => setEditing(b)}>
                                            Edit
                                        </button>
                                        {b.status !== "cancelled" && (
                                            <button
                                                className="hk-btn hk-btn-outline hk-btn-sm"
                                                style={{ color: "var(--hk-danger)" }}
                                                onClick={() => {
                                                    if (window.confirm(`Cancel booking ${b.id}?`)) onStatus(b.id, "cancelled");
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {editing && (
                <BookingEditModal
                    booking={editing}
                    resources={resources}
                    bookings={bookings}
                    onClose={() => setEditing(null)}
                    onSave={(patch) => {
                        onEdit(editing.id, patch);
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}
